import { OrganizationKeyName } from "../../model/Organization";
import { RootState } from "../../store";
import * as authSelectors from "../auth/authSelectors";

export const isUISettingsLoading = (
  state: RootState,
  org: OrganizationKeyName
) => {
  return state.organization.settings[org];
};

export const currentUISettings = (state: RootState) => {
  const o = authSelectors.selectedOrganizationKeyName(state);

  if (!o) {
    return;
  }

  return state.organization.settings[o];
};

export const isDashboardBannerVisible = (state: RootState): boolean => {
  const s = currentUISettings(state);

  if (!s) {
    return false;
  }

  return !!s.dashboardBannerSrc;
};

export const dashboardBannerSrcAndSrcSet = (
  state: RootState
): [string, string] | undefined => {
  const s = currentUISettings(state);

  if (!s) {
    return;
  }

  return [s.dashboardBannerSrc, s.dashboardBannerSrcSet];
};

export const hasMixpanelLink = (state: RootState): boolean => {
  const s = currentUISettings(state);

  if (!s) {
    return false;
  }

  return !!s.mixpanelLink;
};

export const mixpanelLink = (state: RootState): string => {
  const s = currentUISettings(state);

  if (!s) {
    return "";
  }

  return s.mixpanelLink;
};

export const hasFacebookAnalyticsLink = (state: RootState): boolean => {
  const s = currentUISettings(state);

  if (!s) {
    return false;
  }

  return !!s.facebookAnalyticsLink;
};

export const facebookAnalyticsLink = (state: RootState): string => {
  const s = currentUISettings(state);

  if (!s) {
    return "";
  }

  return s.facebookAnalyticsLink;
};

type UserListColumn =
  | "name"
  | "email"
  | "status"
  | "joinDate"
  | "signupCountry";
type UserListAction = "accept" | "reject" | "view";
type UserListFilter = "nameOrEmail" | "country" | "status" | "phoneNb";
type VenueListColumn = "image" | "title" | "tags" | "active" | "address";
type VenueListAction = "edit" | "view";
type VenueListFilter = "name" | "isActive" | "country" | "tag";
type VenueToggableEditField =
  | "showPersonalCode"
  | "showMemberCard"
  | "eventDate";
type UserProfileDetails =
  | "facebook"
  | "userId"
  | "email"
  | "phoneNb"
  | "employeeGroup"
  | "gender";

const uiSettingsShouldElementBeVisible = (
  state: RootState,
  prop: string,
  element: string
): boolean => {
  const s = currentUISettings(state);

  if (!s) {
    return true;
  }

  if (!s[prop]) {
    return true;
  }

  return s[prop].indexOf(element) !== -1;
};

export const isUserListColumnVisible = (
  state: RootState,
  column: UserListColumn
): boolean => {
  return uiSettingsShouldElementBeVisible(state, "userListColumns", column);
};

export const isUserListActionVisible = (
  state: RootState,
  column: UserListAction
): boolean => {
  return uiSettingsShouldElementBeVisible(state, "userListActions", column);
};

export const isUserListFilterVisible = (
  state: RootState,
  column: UserListFilter
): boolean => {
  return uiSettingsShouldElementBeVisible(state, "userListFilters", column);
};

export const isVenueListColumnVisible = (
  state: RootState,
  column: VenueListColumn
): boolean => {
  return uiSettingsShouldElementBeVisible(state, "venueListColumns", column);
};

export const isVenueListActionVisible = (
  state: RootState,
  column: VenueListAction
): boolean => {
  return uiSettingsShouldElementBeVisible(state, "venueListActions", column);
};

export const isVenueListFilterVisible = (
  state: RootState,
  column: VenueListFilter
): boolean => {
  return uiSettingsShouldElementBeVisible(state, "venueListFilters", column);
};

export const isUserProfileFieldVisible = (
  state: RootState,
  column: UserProfileDetails
): boolean => {
  return uiSettingsShouldElementBeVisible(state, "userProfileDetails", column);
};

export const isVenueUpsertFormFieldVisible = (
  state: RootState,
  column: VenueToggableEditField
): boolean => {
  return uiSettingsShouldElementBeVisible(
    state,
    "venueToggableEditFields",
    column
  );
};

export const isUserProfileMixpanelLinkVisible = (state: RootState): boolean => {
  const s = currentUISettings(state);

  if (!s) {
    return false;
  }

  return !!s.isUserProfileMixpanelLinkVisible;
};

export const isUserProfileStripeLinkVisible = (state: RootState): boolean => {
  const s = currentUISettings(state);

  if (!s) {
    return false;
  }

  return !!s.isUserProfileStripeLinkVisible;
};

export const isUserProfileBalanceVisible = (state: RootState): boolean => {
  const s = currentUISettings(state);

  if (!s) {
    return false;
  }

  return !!s.isUserProfileBalanceVisible;
};

export const isVoucherVisibleOnPromotionCode = (state: RootState): boolean => {
  const s = currentUISettings(state);

  if (!s) {
    return false;
  }

  return !!s.isVoucherVisibleOnPromotionCode;
};
